import { useCallback, useState } from "react";

import { Button, MenuItem, Tooltip } from "@mui/material";

import { globalText } from "../../../../assets/texts";
import { useSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useIsFeatureEntitled } from "../../../../Context/TierProvider";
import { type AnalyticsResources } from "../../analyticsResources/types";
import { type AssignLabelsRequest } from "../api";
import { useAssignLabels } from "../hooks";
import { type LabelWithRef } from "../types";
import { AssignLabelsMenu } from "./AssignLabelMenu";

type Props = {
  labels: LabelWithRef[];
  selectedResources: AnalyticsResources[];
  disabled?: boolean;
  variant?: "button" | "menuItem";
  onClose?: () => void;
};

const AssignLabelsButton = ({ labels, selectedResources, disabled = false, variant = "button", onClose }: Props) => {
  const isEntitledAnalyticsLabels = useIsFeatureEntitled("analytics:labels");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const assignLabels = useAssignLabels();
  const snackbar = useSnackbar();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  const handleSave = useCallback(
    ({ add, remove }: { add: string[]; remove: string[] }) => {
      if (add.length === 0 && remove.length === 0) {
        setAnchorEl(null);
        onClose?.();
        return;
      }

      const req: AssignLabelsRequest = {
        addLabels: add,
        removeLabels: remove,
        objects: [],
      };

      selectedResources?.forEach((resource) => {
        req.objects.push({ objectId: resource.id, objectType: resource.contentType });
      });

      assignLabels(
        req,
        () => {
          snackbar.onOpen({
            message: "Successfully assigned labels",
            variant: "success",
            autoHideDuration: 5000,
          });
          setAnchorEl(null);
          onClose?.();
        },
        () => {
          snackbar.onOpen({
            message: "There was an error assigning label(s) to selected resource(s)",
            variant: "error",
            autoHideDuration: 5000,
          });
          setAnchorEl(null);
          onClose?.();
        }
      );
    },
    [selectedResources, setAnchorEl, assignLabels, snackbar, onClose]
  );

  const tooltipTitle = selectedResources.length > 0 && disabled ? globalText.NO_PERMISSION_UPDATE_RESOURCE : "";

  const buttonDisabled = !isEntitledAnalyticsLabels || disabled;

  const renderContent = () => {
    if (variant === "menuItem") {
      return (
        <MenuItem onClick={handleClick} disabled={buttonDisabled} data-cy="assign-labels-menu-item">
          Assign labels
        </MenuItem>
      );
    }

    return (
      <Button
        variant="text"
        id="assign-labels-button"
        aria-controls={open ? "assign-labels-button" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        disabled={buttonDisabled}
        data-cy="assign-labels-button"
      >
        Assign labels
      </Button>
    );
  };

  return (
    <>
      <Tooltip arrow placement="top" title={tooltipTitle}>
        <span>{renderContent()}</span>
      </Tooltip>
      {open && (
        <AssignLabelsMenu
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          labels={labels}
          selectedResources={selectedResources}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default AssignLabelsButton;
